import React from 'react';
import { CustomFieldDefinition } from '../../types/custom-fields';
import RichTextEditor from '../RichTextEditor';

interface CustomFieldInputProps {
  field: CustomFieldDefinition;
  value: any;
  onChange: (value: any) => void;
}

const CustomFieldInput: React.FC<CustomFieldInputProps> = ({
  field,
  value,
  onChange
}) => {
  const { field_type, field_config, name, description } = field;
  
  const renderInput = () => {
    switch (field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field_config.placeholder || 'Enter text...'}
            className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent"
          />
        );
      
      case 'richtext':
        return (
          <RichTextEditor
            value={value || ''}
            onChange={onChange}
            placeholder={field_config.placeholder || 'Enter formatted text...'}
            maxLength={field_config.max_length || 3000}
            required={field_config.required || false}
            className="mt-1"
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
            placeholder={field_config.placeholder || 'Enter number...'}
            min={field_config.min_value}
            max={field_config.max_value}
            className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent"
          />
        );
      
      case 'currency':
        return (
          <div className="relative">
            {field_config.prefix && (
              <span className="absolute left-3 top-2 text-text-secondary dark:text-text-dark-secondary">
                {field_config.prefix}
              </span>
            )}
            <input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : '')}
              placeholder={field_config.placeholder || '0.00'}
              min={field_config.min_value}
              max={field_config.max_value}
              step={field_config.decimal_places ? `0.${'0'.repeat(field_config.decimal_places - 1)}1` : '0.01'}
              className={`w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent ${field_config.prefix ? 'pl-8' : ''}`}
            />
          </div>
        );
      
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent"
          >
            <option value="">Select an option...</option>
            {field_config.options?.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      
      case 'multi_select':
        return (
          <div className="space-y-2">
            {field_config.options?.map((option) => (
              <label key={option} className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={Array.isArray(value) ? value.includes(option) : false}
                  onChange={(e) => {
                    const currentValues = Array.isArray(value) ? value : [];
                    if (e.target.checked) {
                      onChange([...currentValues, option]);
                    } else {
                      onChange(currentValues.filter(v => v !== option));
                    }
                  }}
                  className="h-4 w-4 text-primary-700 dark:text-primary-600 border-border-light dark:border-border-dark rounded focus:ring-primary-500 dark:focus:ring-primary-400"
                />
                <span className="ml-2 text-text-secondary dark:text-text-dark-secondary">
                  {option}
                </span>
              </label>
            ))}
          </div>
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:border-transparent"
          />
        );
      
      case 'boolean':
        return (
          <div className="flex items-center space-x-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                checked={value === true}
                onChange={() => onChange(true)}
                className="h-4 w-4 text-primary-700 dark:text-primary-600 border-border-light dark:border-border-dark focus:ring-primary-500 dark:focus:ring-primary-400"
              />
              <span className="ml-2 text-text-secondary dark:text-text-dark-secondary">
                Yes
              </span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="radio"
                checked={value === false}
                onChange={() => onChange(false)}
                className="h-4 w-4 text-primary-700 dark:text-primary-600 border-border-light dark:border-border-dark focus:ring-primary-500 dark:focus:ring-primary-400"
              />
              <span className="ml-2 text-text-secondary dark:text-text-dark-secondary">
                No
              </span>
            </label>
          </div>
        );
      
      default:
        return (
          <p className="text-text-tertiary dark:text-text-dark-tertiary text-sm">
            Unsupported field type: {field_type}
          </p>
        );
    }
  };
  
  return (
    <div>
      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1">
        {name}
        {field_config.required && (
          <span className="text-red-600 dark:text-red-400 ml-1">*</span>
        )}
      </label>
      {description && (
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-2">
          {description}
        </p>
      )}
      {renderInput()}
      {field_config.help_text && (
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
          {field_config.help_text}
        </p>
      )}
    </div>
  );
};

export default CustomFieldInput;


